import { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import AdSpace from '@/components/AdSpace';
import SubscribeBox from '@/components/SubscribeBox';
import HotTopics from '@/components/HotTopics';
import SEO from '@/components/SEO';
import { getArticles, incrementPageViews } from '@/lib/api';
import { Loader2, Flame, Eye, Play } from 'lucide-react';

const BATCH_SIZE = 20;

/* ── Hero ── */
const WorldstarHero = ({ article }) => {
  if (!article) return null;
  const url = `/article/${article.slug || article.id}`;
  const formattedDate = article.published_at
    ? format(new Date(article.published_at), 'MMM d, yyyy')
    : '';

  return (
    <div className="relative w-full bg-black overflow-hidden border-b border-[#1a1a1a]" data-testid="worldstar-hero">
      <Link to={url} className="relative z-10 flex flex-col w-full animate-fade-in-up group" data-testid="worldstar-hero-link">
        <div className="relative w-full h-[40vh] sm:h-[52vh] overflow-hidden shadow-[0_0_60px_rgba(202,138,4,0.18)] bg-black">
          {article.featured_image ? (
            <>
              <img src={article.featured_image} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover blur-2xl brightness-[0.45] saturate-150" />
              <img src={article.featured_image} alt={article.title} className="relative z-[1] w-full h-full object-contain transition-transform duration-700 group-hover:scale-105" />
            </>
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-[#1a1200] to-black flex items-center justify-center">
              <span className="text-yellow-600 text-5xl font-black">W</span>
            </div>
          )}
          <div className="absolute inset-0 bg-black/10 group-hover:bg-black/30 transition-colors duration-300" />
          <span className="absolute top-4 right-4 flex items-center gap-1.5 bg-black/70 backdrop-blur-sm text-white text-[10px] font-bold uppercase tracking-wider px-2.5 py-1.5">
            <Play size={10} fill="currentColor" className="text-yellow-500" /> Video
          </span>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="relative">
              <div className="absolute inset-0 rounded-full bg-yellow-600/50 blur-xl scale-125 group-hover:scale-[1.8] transition-transform duration-500" />
              <div className="relative w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-white/95 flex items-center justify-center shadow-2xl group-hover:scale-110 transition-transform duration-300">
                <Play size={26} fill="black" className="text-black ml-1" />
              </div>
            </div>
          </div>
        </div>
        <div className="relative z-10 bg-black px-4 sm:px-10 py-6 sm:py-8 max-w-4xl mx-auto w-full">
          <span className="inline-flex items-center gap-2 bg-yellow-600 text-white text-[10px] sm:text-xs font-bold uppercase tracking-widest px-2.5 py-1 mb-4">
            <Play size={12} fill="currentColor" /> Latest Video
          </span>
          <h2 className="text-2xl sm:text-4xl lg:text-5xl font-black text-white leading-tight mb-4">
            {article.title}
          </h2>
          <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-500 mb-5">
            <Eye size={13} className="text-green-500" />
            <span className="text-green-500 font-medium">{(article.views || 0).toLocaleString()} views</span>
            <span>·</span>
            <span>{formattedDate}</span>
          </div>
          <span className="inline-flex items-center gap-2 bg-yellow-600 text-white text-sm font-bold px-6 py-3 group-hover:bg-yellow-500 transition-colors duration-300">
            <Play size={14} fill="currentColor" /> Watch Now
          </span>
        </div>
      </Link>
    </div>
  );
};

/* ── Page ── */
const WorldstarPage = () => {
  const [allArticles, setAllArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [visibleCount, setVisibleCount] = useState(BATCH_SIZE);
  const sentinelRef = useRef(null);

  useEffect(() => {
    const fetchArticles = async () => {
      try {
        const res = await getArticles({ category: 'Cincy Worldstar', limit: 500 });
        const sorted = [...res.data].sort((a, b) => new Date(b.published_at) - new Date(a.published_at));
        setAllArticles(sorted);
      } catch (error) {
        console.error('Failed to fetch articles:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchArticles();
    incrementPageViews('worldstar').catch(() => {});
  }, []);

  const loadMore = useCallback(() => {
    setVisibleCount(prev => Math.min(prev + BATCH_SIZE, allArticles.length));
  }, [allArticles.length]);

  useEffect(() => {
    if (!sentinelRef.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) loadMore(); },
      { rootMargin: '200px' }
    );
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [loadMore]);

  const heroArticle = allArticles[0] || null;
  const restArticles = allArticles.slice(1, visibleCount);
  const hasMore = visibleCount < allArticles.length;

  return (
    <div className="min-h-screen bg-black page-enter" data-testid="worldstar-page">
      <SEO title="Cincy Worldstar" description="Check On Yo Peoples TV - Cincinnati's wildest content" />
      <Header />

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-yellow-500" />
        </div>
      ) : (
        <>
          {/* Hero — latest Worldstar video */}
          <WorldstarHero article={heroArticle} />

          <main className="max-w-4xl mx-auto px-4 py-6">
            <AdSpace type="header" className="mb-6" />

            {restArticles.length > 0 ? (
              <>
                <div className="flex items-center gap-2 mb-4 py-2 border-b border-[#333]">
                  <Flame size={16} className="text-yellow-500" />
                  <span className="text-white font-bold text-sm">All Clips</span>
                  <span className="text-gray-600 text-xs ml-auto">{allArticles.length - 1} more</span>
                </div>

                <div className="flex flex-col gap-3" data-testid="worldstar-video-list">
                  {restArticles.map((article, idx) => (
                    <Link
                      key={article.id}
                      to={`/article/${article.slug || article.id}`}
                      className={`group block animate-fade-in-up stagger-${Math.min(idx + 1, 8)}`}
                      data-testid={`worldstar-video-${article.id}`}
                    >
                      <div className="flex bg-[#111] border border-[#222] hover:border-yellow-500/50 transition-all duration-300 overflow-hidden">
                        <div className="relative w-[45%] sm:w-[40%] flex-shrink-0 aspect-video overflow-hidden bg-[#0a0a0a]">
                          {article.featured_image ? (
                            <img
                              src={article.featured_image}
                              alt={article.title}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                              loading="lazy"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Play size={36} className="text-gray-700" />
                            </div>
                          )}
                          <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                            <div className="w-12 h-12 rounded-full bg-yellow-500/90 flex items-center justify-center shadow-lg">
                              <Play size={22} fill="black" className="text-black ml-0.5" />
                            </div>
                          </div>
                        </div>
                        <div className="flex-1 p-3 sm:p-4 flex flex-col justify-center min-w-0">
                          <h3 className="text-sm sm:text-base font-bold text-white group-hover:text-yellow-500 transition-colors leading-tight line-clamp-2 mb-2">
                            {article.title}
                          </h3>
                          <div className="flex items-center gap-3 text-[11px] sm:text-xs text-gray-500">
                            <span className="flex items-center gap-1">
                              <Eye size={12} className="text-yellow-500/70" />
                              {article.views || 0}
                            </span>
                            <span>
                              {article.published_at
                                ? new Date(article.published_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                                : 'Draft'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>

                {hasMore && (
                  <div ref={sentinelRef} className="flex items-center justify-center py-8" data-testid="load-more-sentinel">
                    <Loader2 className="w-6 h-6 animate-spin text-yellow-500" />
                  </div>
                )}
              </>
            ) : !heroArticle ? (
              <div className="text-center py-20 border border-[#222] bg-[#111]">
                <p className="text-gray-500">No Worldstar content yet.</p>
                <p className="text-gray-600 text-sm mt-2">Add Cincy Worldstar stories in admin</p>
              </div>
            ) : null}

            <AdSpace type="footer" className="mt-6" />
            <HotTopics />
            <div className="mt-4">
              <SubscribeBox />
            </div>
          </main>
        </>
      )}

      <Footer />
    </div>
  );
};

export default WorldstarPage;
